/**
 * Receipt data-access layer for the Generate Receipt flow on a transaction row.
 *
 * Two independent one-shot operations over the same `paymentId`: email the receipt to the member,
 * or fetch it as a base64 PDF for a browser-native download. Neither caches — a receipt is an
 * action, not a resource the UI holds on to.
 */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  DownloadPaymentReceiptDocument,
  type DownloadPaymentReceiptFailureCode,
  SendPaymentReceiptEmailDocument,
  type SendPaymentReceiptEmailFailureCode,
} from '@/graphql/generated/graphql';

/** The address a receipt was emailed to, as reported by the server. */
export interface ReceiptEmailResult {
  email: string;
}

/** A receipt PDF, base64-encoded exactly as Apex produced it, plus the filename to save it under. */
export interface ReceiptDownloadResult {
  document: string;
  filename: string;
}

/**
 * Thrown when sendPaymentReceiptEmail returns its Failure variant. Carries the raw wire code
 * (`NOT_FOUND` | `FAILED`) — callers prefix it to `PAYMENT_RECEIPT_*` for i18n lookup, since bare
 * `NOT_FOUND` already means something generic in errors.json.
 */
export class PaymentReceiptEmailError extends Error {
  constructor(public readonly code: SendPaymentReceiptEmailFailureCode) {
    super(code);
    this.name = 'PaymentReceiptEmailError';
  }
}

/**
 * Thrown when downloadPaymentReceipt returns its Failure variant. `NOT_FOUND` deliberately
 * overloads unknown id / owned-by-another-member / malformed id to avoid an enumeration oracle, so
 * there is nothing for the client to disambiguate.
 */
export class PaymentReceiptDownloadError extends Error {
  constructor(public readonly code: DownloadPaymentReceiptFailureCode) {
    super(code);
    this.name = 'PaymentReceiptDownloadError';
  }
}

/** Emails the authenticated member a receipt for one of their own past payments. */
export async function requestSendPaymentReceiptEmail(paymentId: string): Promise<ReceiptEmailResult> {
  const data = await gqlRequest(SendPaymentReceiptEmailDocument, { paymentId });
  const result = data.sendPaymentReceiptEmail;
  if (result.__typename === 'SendPaymentReceiptEmailFailure') {
    throw new PaymentReceiptEmailError(result.code);
  }
  return { email: result.email };
}

/**
 * Fetches the receipt PDF for one of the member's own past payments.
 *
 * This is a GraphQL `query`, but it is only ever fired imperatively from a menu click, so callers
 * wrap it in `useMutation` rather than `useQuery` — there is nothing to cache or refetch.
 */
export async function requestDownloadPaymentReceipt(paymentId: string): Promise<ReceiptDownloadResult> {
  const data = await gqlRequest(DownloadPaymentReceiptDocument, { paymentId });
  const result = data.downloadPaymentReceipt;
  if (result.__typename === 'DownloadPaymentReceiptFailure') {
    throw new PaymentReceiptDownloadError(result.code);
  }
  return { document: result.document, filename: result.filename };
}
